'use client';

import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { authClient } from '@/lib/auth-client';
import { Loader2 } from 'lucide-react';
interface VkLoginButtonProps {
  callbackURL?: string;
  onError?: (message: string) => void;
  variant?: 'large' | 'round';
}

/**
 * Кнопка входа через ВКонтакте.
 * Использует встроенный VK social provider Better Auth (signIn.social).
 * Скрывается, если NEXT_PUBLIC_VK_CLIENT_ID не задан.
 *
 * variant='large' — полноширинная кнопка с текстом.
 * variant='round' — круглая иконка 48×48 для компактного ряда провайдеров.
 */
export function VkLoginButton({
  callbackURL = '/',
  onError,
  variant = 'large'
}: VkLoginButtonProps) {
  const [loading, setLoading] = useState(false);

  // Hide button when VK OAuth is not configured (applies to both variants)
  if (!process.env.NEXT_PUBLIC_VK_CLIENT_ID) {
    return null;
  }
  const handleLogin = async () => {
    setLoading(true);
    try {
      const result = await authClient.signIn.social({
        provider: 'vk',
        callbackURL
      });
      // If client didn't auto-redirect, handle it manually
      if (result?.data?.url) {
        window.location.href = result.data.url;
      } else if (result?.error) {
        const message = result.error.message || '';
        const isCancelled = message.includes('cancel') || message.includes('denied');
        if (isCancelled) {
          onError?.('Авторизация через ВКонтакте отменена');
        } else {
          onError?.('Ошибка авторизации через ВКонтакте. Попробуйте позже');
        }
        setLoading(false);
      } else {
        console.error('[VkLogin] Unexpected result:', JSON.stringify(result));
      }
    } catch (err) {
      console.error('[VkLogin] Error:', err);
      onError?.('Ошибка авторизации через ВКонтакте. Попробуйте позже');
      setLoading(false);
    }
  };
  if (variant === 'round') {
    return <button type="button" onClick={handleLogin} disabled={loading} aria-label="Войти через ВКонтакте" className="flex items-center justify-center w-14 h-14 rounded-full bg-white hover:bg-slate-50 dark:bg-zinc-900 dark:hover:bg-zinc-800 disabled:opacity-50 text-slate-800 dark:text-zinc-100 border border-slate-200 dark:border-zinc-700 shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:shadow-[0_4px_12px_rgba(15,23,42,0.08)] hover:border-slate-300 dark:hover:border-zinc-600 transition-all cursor-pointer disabled:cursor-not-allowed">
        {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <VkIcon className="w-8 h-8" />}
      </button>;
  }
  return <button onClick={handleLogin} disabled={loading} className="flex items-center justify-center gap-3 w-full h-[52px] px-5 bg-white hover:bg-slate-50 dark:bg-zinc-900 dark:hover:bg-zinc-800 disabled:opacity-50 text-slate-800 dark:text-zinc-100 border border-slate-200 dark:border-zinc-700 rounded-2xl text-[15px] font-medium tracking-tight shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:shadow-[0_4px_12px_rgba(15,23,42,0.08)] hover:border-slate-300 dark:hover:border-zinc-600 transition-all cursor-pointer disabled:cursor-not-allowed">
      {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <VkIcon className="w-5 h-5" />}
      Войти через ВКонтакте
    </button>;
}
function VkIcon(t0) {
  const $ = _c(4);
  const {
    className
  } = t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <circle cx="12" cy="12" r="12" fill="#0077FF" />;
    t2 = <path fill="white" transform="translate(12 12) scale(1.10) translate(-12.33 -12.5)" d="M18.15 16.27H16.69C16.14 16.27 15.97 15.82 14.97 14.82C14.1 13.98 13.74 13.87 13.53 13.87C13.24 13.87 13.16 13.95 13.16 14.35V15.67C13.16 16.01 13.05 16.21 12.17 16.21C10.71 16.21 9.1 15.33 7.96 13.69C6.25 11.3 5.78 9.5 5.78 9.13C5.78 8.93 5.86 8.74 6.26 8.74H7.72C8.08 8.74 8.22 8.91 8.36 9.29C9.07 11.36 10.28 13.17 10.78 13.17C10.97 13.17 11.05 13.09 11.05 12.63V10.5C11 9.5 10.47 9.42 10.47 9.07C10.47 8.9 10.61 8.74 10.84 8.74H13.13C13.43 8.74 13.54 8.9 13.54 9.25V12.13C13.54 12.43 13.67 12.54 13.76 12.54C13.94 12.54 14.1 12.43 14.44 12.09C15.47 10.93 16.21 9.14 16.21 9.14C16.3 8.93 16.47 8.74 16.83 8.74H18.29C18.73 8.74 18.82 8.96 18.73 9.27C18.55 10.12 16.78 12.61 16.78 12.61C16.63 12.86 16.57 12.97 16.78 13.25C16.92 13.46 17.4 13.87 17.72 14.24C18.31 14.91 18.76 15.47 18.88 15.86C19 16.24 18.81 16.27 18.15 16.27Z" />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] !== className) {
    t3 = <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">{t1}{t2}</svg>;
    $[2] = className;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
}